/**
 * Subscription gate (funnel step 2 enforcement).
 *
 * Wraps the protected `/app` subtree, OUTSIDE `OnboardingGate`, so the
 * funnel order is enforced: a signed-in user who has never picked a plan
 * + paid the $1 trial is bounced to `/packages` before onboarding even
 * matters. A trialing OR active subscription both satisfy the gate
 * (`has_subscription` is true for either) — the trial gate, not this one,
 * is what blocks scene/video creation during the trial.
 *
 * Defensive on purpose: we redirect ONLY when the subscription query has
 * positively resolved with `has_subscription === false`. On an ERROR we
 * deliberately do NOT redirect — trapping the user in a redirect loop when
 * the billing endpoint is briefly unavailable would be worse than letting
 * them through to a page the trial gate still guards.
 *
 * While the query is LOADING we render nothing (mirroring `ProtectedRoute`).
 * This is load-bearing for funnel ordering: the nested `OnboardingGate`
 * reads a separate, faster `whoami` query, so if we let children render
 * during the subscription load it would redirect a brand-new unpaid user to
 * onboarding before the payment check ever resolved. Blocking here means
 * `OnboardingGate` only mounts once a subscription is confirmed.
 */
import { Navigate } from 'react-router';

import { paths } from '@/config/paths';

import { useSubscription } from '../api/billing-api';

import type { ReactNode } from 'react';

export function SubscriptionGate({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useSubscription();

  if (isLoading) {
    return null;
  }

  if (!isError && data && !data.has_subscription) {
    return <Navigate to={paths.packages} replace />;
  }

  return <>{children}</>;
}

export default SubscriptionGate;
